<template>
  <div class="extracted-data-tree">
    <template v-if="entries.length">
      <div
        v-for="entry in entries"
        :key="entry.key"
        class="data-entry"
        :style="{ paddingLeft: `${depth * 14}px` }">
        <template v-if="entry.isNested">
          <div class="data-entry__key text-caption text-weight-medium">{{ humanizeKey(entry.key) }}</div>
          <ExtractedDataTree
            :data="entry.value"
            :depth="depth + 1" />
        </template>
        <template v-else>
          <span class="data-entry__key text-caption text-grey-6">{{ humanizeKey(entry.key) }}</span>
          <span class="data-entry__value text-body2">{{ formatValue(entry.value) }}</span>
        </template>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

/**
 * Generic readable rendering of extracted data — nested objects indent,
 * primitives render as label/value rows. Used when no model-specific
 * extraction component is registered (or as its error fallback).
 */
const props = withDefaults(defineProps<{ data: unknown; depth?: number }>(), {
  depth: 0,
})

interface Entry {
  key: string
  value: unknown
  isNested: boolean
}

const isNestedValue = (value: unknown): boolean =>
  value !== null && typeof value === 'object' && (Array.isArray(value) ? value.length > 0 : Object.keys(value as object).length > 0)

const entries = computed<Entry[]>(() => {
  if (props.data === null || typeof props.data !== 'object') return []

  if (Array.isArray(props.data)) {
    return props.data.map((value, index) => ({
      key: String(index + 1),
      value,
      isNested: isNestedValue(value),
    }))
  }

  return Object.entries(props.data as Record<string, unknown>).map(([key, value]) => ({
    key,
    value,
    isNested: isNestedValue(value),
  }))
})

const humanizeKey = (key: string): string => key.replace(/_/g, ' ').replace(/^\w/, (char) => char.toUpperCase())

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? '✓' : '✗'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}
</script>

<style lang="scss" scoped>
.data-entry {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  padding-top: 2px;
  padding-bottom: 2px;

  &__key {
    min-width: 130px;
  }

  &__value {
    word-break: break-word;
  }
}
</style>
